import { Injectable } from '@angular/core';
import { DatabaseService } from '../alcance/services/database.service';
import { Filesystem } from '@capacitor/filesystem';
import { Observable, catchError, firstValueFrom, from, map, of, throwError } from 'rxjs';
import { getAppInjector } from '../app-injector';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class FilaDBService extends DatabaseService {
    override classname: string = 'FilaDBService';
    override table = 'fila';
    override typeKey = 'TEXT';
    protected override useUuidKey = false;

    public isSend = false;
    private retryBlockedUntil = 0;

    override indices = [
    ];

    private createObservable(reg: any, send: any): Observable<any>{
        let observable: Observable<any>;

        switch (reg.method) {
            case 'POST':
                observable = reg.service.post(send, reg.path, {skipbase: true}, {showLoader: 'false', showError: 'false'});
                break;

            case 'PUT':
                observable = reg.service.put(send, reg.path, null, {showLoader: 'false', showError: 'false'}).pipe(
                    catchError((error: HttpErrorResponse): Observable<any> => {
                        if(error.status == 404){
                            return of(null);
                        }

                        return throwError(() => error);
                    })
                );
                break;

            case 'DELETE':
            default:
                observable = reg.service.delete(send, reg.path, null, {showLoader: 'false', showError: 'false'}).pipe(
                    catchError((error: HttpErrorResponse): Observable<any> => {
                        if(error.status == 404){
                            return of(null);
                        }

                        return throwError(() => error);
                    })
                );
                break;
        }

        return observable;
    }

    /**
     * Percorre o objeto e troca caminhos file:// por base64.
     * Etapa 1: mantém compatibilidade com o fluxo atual.
     * Etapa futura: substituir por upload temporário e enviar somente UUIDs dos arquivos.
     */
    private async searchFiles(obj: any): Promise<any>{
        if(!obj){
            return obj;
        }

        if(Array.isArray(obj)){
            const items: any[] = [];
            for(const item of obj){
                items.push(await this.searchFiles(item));
            }
            return items;
        }

        if(typeof obj !== 'object'){
            return obj;
        }

        for(const key of Object.keys(obj)){
            const value = obj[key];

            if(Array.isArray(value) || (value && typeof value === 'object')){
                obj[key] = await this.searchFiles(value);
                continue;
            }

            if((key == 'file' || key == 'evidencia') && typeof value === 'string' && value.indexOf('file://') != -1){
                const contents = await Filesystem.readFile({path: value});
                obj[key] = contents.data;
            }
        }

        return obj;
    }

    private async processRegister(reg: any): Promise<void>{
        const send = await this.searchFiles({...reg.data});
        const response = await firstValueFrom(this.createObservable(reg, send));

        if(reg.method === 'POST'){
            if(!reg.path && reg.db){
                /**
                 * Com UUID:
                 * - uuid é a chave local e NÃO deve ser substituída pelo id do servidor.
                 * - id é apenas o id numérico do servidor.
                 */
                const localUuid = reg.data?.uuid;

                if(response && typeof response === 'object'){
                    reg.data = Object.assign({}, reg.data, response);
                }

                if(localUuid && !reg.data.uuid){
                    reg.data.uuid = localUuid;
                }

                if(response?.id){
                    reg.data.id = response.id;
                }

                await reg.db.put(reg.data);
            }

            await this.del(reg.id);
            return;
        }

        if(reg.method === 'PUT'){
            if(response === null){
                await this.del(reg.id);
                return;
            }

            if(reg.db){
                const localUuid = reg.data?.uuid;

                if(response && typeof response === 'object'){
                    reg.data = Object.assign({}, reg.data, response);
                }

                if(localUuid && !reg.data.uuid){
                    reg.data.uuid = localUuid;
                }

                await reg.db.put(reg.data);
            }

            await this.del(reg.id);
            return;
        }

        await this.del(reg.id);
    }

    async send(): Promise<any> {
        if (!this.initialized) {
            await this.createTable();
            return this.send();
        }

        if (this.isSend || Date.now() < this.retryBlockedUntil) {
            return this.isSend;
        }

        this.isSend = true;

        try {
            const registros = await this.getAll();
            const persistedBlock = registros.data
                .filter((reg: any) => reg.last_status === 429 && reg.next_retry_at)
                .reduce((latest: number, reg: any) => Math.max(latest, Date.parse(reg.next_retry_at) || 0), 0);

            if(persistedBlock > Date.now()){
                this.retryBlockedUntil = persistedBlock;
                return false;
            }

            /**
             * Envio sequencial.
             * Evita vários uploads/base64 em paralelo e reduz chance de 429/timeouts.
             */
            for(const reg of registros.data){
                try{
                    await this.processRegister(reg);
                }catch(error: any){
                    if(error?.status === 429){
                        const retryAfter = Number(error?.headers?.get?.('Retry-After'));
                        const delay = Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : 30;
                        this.retryBlockedUntil = Date.now() + delay * 1000;
                        reg.last_status = 429;
                        reg.next_retry_at = new Date(this.retryBlockedUntil).toISOString();
                        await this.put(reg);
                        break;
                    }

                    throw error;
                }
            }
        } finally {
            this.isSend = false;
        }

        return this.isSend;
    }

    override defineObj(obj: any){
        obj.service = getAppInjector().get(obj.service);
        if(obj.db){
            obj.db = getAppInjector().get(obj.db);
        }
        return obj;
    }
}
